import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import {
  Target,
  Users,
  FolderGit,
  Landmark,
  ShieldAlert,
  FileText,
  Truck,
  Lightbulb,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NodeTooltip } from "../interactions/node-tooltip";

const categoryIconMap: Record<string, any> = {
  strategy: Target,
  people: Users,
  work: FolderGit,
  sales: Users,
  finance: Landmark,
  operations: Truck,
  risk: ShieldAlert,
  documents: FileText,
  innovation: Lightbulb,
};

export const CustomNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as any;
  const type = (nodeData.type as string) || "work";
  const Icon = categoryIconMap[type] || FolderGit;

  return (
    <div className="relative group">
      {/* Target port (Top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-2.5 h-2.5 bg-border-strong border-2 border-card rounded-full !min-h-0 !min-w-0"
      />

      <NodeTooltip data={nodeData}>
        <Card
          className={cn(
            "w-56 overflow-hidden border border-border shadow-md bg-card transition-all",
            selected
              ? "ring-2 ring-primary-500 border-primary-500 scale-[1.02]"
              : "hover:border-primary-500/50",
          )}
        >
          {/* Top accent line based on category */}
          <div
            className={cn(
              "h-1.5 w-full",
              type === "strategy"
                ? "bg-category-strategy"
                : type === "people"
                  ? "bg-category-people"
                  : type === "work"
                    ? "bg-category-work"
                    : type === "sales"
                      ? "bg-category-sales"
                      : type === "finance"
                        ? "bg-category-finance"
                        : type === "operations"
                          ? "bg-category-operations"
                          : type === "risk"
                            ? "bg-category-risk"
                            : type === "documents"
                              ? "bg-category-documents"
                              : "bg-category-innovation",
            )}
          />
          <CardContent className="p-4 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                {type}
              </span>
              <div className="h-6 w-6 rounded-md bg-card-muted flex items-center justify-center text-text-secondary border border-border/50">
                <Icon className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* Node name */}
            <h4 className="text-sm font-bold text-text-primary truncate leading-tight">
              {nodeData.label as string}
            </h4>

            {/* Optional metadata preview */}
            {nodeData.subtext && (
              <p className="text-[10px] text-text-secondary truncate">
                {nodeData.subtext as string}
              </p>
            )}

            {/* Status Badge */}
            {nodeData.status && (
              <div className="flex items-center gap-1.5 pt-1">
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    nodeData.status === "healthy" ||
                      nodeData.status === "on_track"
                      ? "bg-success"
                      : nodeData.status === "attention" ||
                          nodeData.status === "watch"
                        ? "bg-warning"
                        : "bg-error",
                  )}
                />
                <span className="text-[10px] font-medium text-text-secondary capitalize">
                  {nodeData.status as string}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </NodeTooltip>

      {/* Source port (Bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2.5 h-2.5 bg-border-strong border-2 border-card rounded-full !min-h-0 !min-w-0"
      />
    </div>
  );
});

CustomNode.displayName = "CustomNode";
